<template>
  <div v-if="loading" class="page-loading">
    <Loading />
  </div>
  <div v-if="!isAppInfoVisible">
    <div>
      <div class="strip fs-14 between-flex">
        <div class="w-25">
          <SearchAndFilter
            v-model="searchQuery"
            :showInput="true"
            :showSelect="false"
            :nullable="true"
            :placeholder="$t('List.searchByListName')"
            @submitForm="handleSearchFilterChange"
          />
        </div>
        <div class="right-strip">
          <div class="filter-container">
            <FilterComponent
              :sortCriteria="sortCriteria"
              :sortOrder="sortOrder"
              :sortingOptions="sortingOptions"
              :orderOptions="orderOptions"
              :isOrderOptions="true"
              @filter-change="handleFilterChange"
            />
            <MultiSelectFilterComponent
              :available-filters="platformFilters"
              :initial-selected="selectedFilters"
              :placeholder-text="$t('Custom.allPlatforms')"
              @update:selected-filters="handlePlatformFilterChange"
            />
          </div>
          <a href="#" @click.prevent="openModal('create')">
            <span class="material-symbols-outlined"> playlist_add </span>
            {{ $t("Custom.new") }}
          </a>
        </div>
      </div>
      <NotFoundContent
        v-if="!userAppList.length"
        :message="$t('List.listIsEmpty')"
      />
      <NotFoundContent
        v-else-if="filteredAndSortedItems.length === 0"
        :message="$t('Custom.notFoundResult')"
      />
      <div v-else class="list-items-content">
        <TableComponent>
          <template v-slot:table-header>
            <tr>
              <th v-for="header in tableHeaders" :key="header.key">
                {{ header.label }}
              </th>
            </tr>
          </template>
          <template v-slot:table-body>
            <tr
              v-for="(data, index) in filteredAndSortedItems"
              :key="data.list.id"
              :class="{ 'zebra-stripe': index % 2 === 0 }"
            >
              <td>{{ index + 1 }}</td>
              <td>
                <template
                  v-if="getStoreImage(data?.list?.platformTypeId).length"
                >
                  <div
                    class="action-buttons"
                    style="justify-content: flex-start"
                  >
                    <img
                      v-for="(src, indx) in getStoreImage(
                        data?.list?.platformTypeId
                      )"
                      :key="indx"
                      :src="src"
                      alt="Platform Icon"
                      class="platform-icon"
                    />
                  </div>
                </template>
              </td>
              <td>
                <a
                  href="javascript:void(0)"
                  class="clickable-text"
                  @click.stop="
                    showItems(
                      data.list.id,
                      data.sharingPermissionId,
                      data.list.name
                    )
                  "
                  >{{ data.list.name || "N/A" }}</a
                >
              </td>
              <td>{{ data.list.appCount || 0 }}</td>
              <td>
                <div
                  :class="[
                    'action-state',
                    permissionClass(data.sharingPermissionId),
                  ]"
                >
                  <span
                    v-if="!data.isOwner"
                    class="material-symbols-outlined fs-15 mr-5 icon"
                    >share</span
                  >
                  {{ permissionText(data.sharingPermissionId) }}
                </div>
              </td>
              <td>{{ formatDate(data.list.created_at) || "N/A" }}</td>
              <td>
                <div class="action-buttons">
                  <div
                    class="action-button"
                    :class="{
                      green: data?.isNotification,
                      red: !data?.isNotification,
                    }"
                    :title="$t('Notification.name')"
                    @click.stop="
                      handleNotification(
                        data.listId,
                        data.userId,
                        data.isNotification,
                        index
                      )
                    "
                  >
                    <template v-if="loadingIndex === index">
                      <span class="button-spinner"></span>
                    </template>
                    <template v-else>
                      <span class="material-symbols-outlined notification-icon">
                        {{
                          data.isNotification
                            ? "notifications_active"
                            : "notification_add"
                        }}
                      </span>
                      <span>{{
                        data.isNotification ? "Is On" : "Is Off"
                      }}</span>
                    </template>
                  </div>
                  <div
                    v-if="permissionAccess(data.sharingPermissionId)"
                    class="action-button"
                    @click.stop="shareList(data.list.id)"
                  >
                    <span
                      class="material-symbols-outlined share-icon"
                      :title="$t('Custom.share')"
                      >share</span
                    >
                  </div>
                  <div
                    v-if="permissionAccess(data.sharingPermissionId)"
                    class="action-button"
                    @click.stop="openModal('update', data)"
                  >
                    <span
                      class="material-symbols-outlined edit-icon"
                      :title="$t('Custom.edit')"
                      >edit_square</span
                    >
                  </div>
                  <div
                    v-if="permissionAccess(data.sharingPermissionId)"
                    class="action-button"
                    @click.stop="handleAreYouSureListModal(data.list)"
                  >
                    <span
                      class="material-symbols-outlined remove-icon"
                      :title="$t('Custom.delete')"
                      >delete_sweep</span
                    >
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </TableComponent>
        <div class="pagination-wrapper">
          <PaginationComponent
            :meta="pagination"
            @page-changed="fetchAppList"
          />
        </div>
      </div>
    </div>
  </div>

  <Modal
    :isOpen="isModalOpen"
    :modalTitle="modalTitle"
    :actionType="actionType"
    @close="closeModal"
    @action="handleModalAction"
  >
    <TextInput
      :label="$t('List.listName')"
      v-model="listName"
      :required="true"
    />
    <SelectInput
      :label="$t('Platform.platformType')"
      v-model="selectedOption"
      :options="platformTypes"
      optionName="platformTypeName"
      :placeholder="$t('Platform.selectAPlatformType')"
      :isDisabled="actionType === 'update'"
      :required="true"
    />
  </Modal>

  <Modal
    :isOpen="isItemsModalOpen"
    :modalTitle="modalTitle"
    :actionButton="false"
    @close="closeModal"
  >
    <div>
      <NotFoundContent
        v-if="listItems.length === 0"
        :message="$t('List.listIsEmpty')"
      />
      <div v-else>
        <div class="mb-10">
          <SearchAndFilter
            v-model="searchItemQuery"
            v-model:modelValueSort="sortOrder"
            :showInput="true"
            :showSelect="false"
            :placeholder="$t('Apps.searchByAppName')"
          />
        </div>
        <NotFoundContent
          v-if="filteredAndSortedListItems.length === 0"
          :message="$t('Custom.notFoundResult')"
        />
        <div class="list-items">
          <ItemCard
            v-for="data in filteredAndSortedListItems"
            :key="data.id"
            :data="data"
            :item="data.item"
            :menuId="appMenuId"
            :isOptionMenu="true"
            @setItemMenu="setOpenAppMenu"
            @toggleView="handleShow"
            :buttons="generateButtons(data)"
          />
        </div>
      </div>
    </div>
  </Modal>

  <Modal
    :isOpen="ShareModal"
    :modalTitle="modalTitle"
    :actionButton="false"
    @close="closeModal"
  >
    <div class="invite-section">
      <div class="input-select-container">
        <input
          type="email"
          v-model="inviteEmail"
          placeholder="Enter email"
          class="invite-input"
        />
        <select v-model="inviteRole" class="role-select">
          <option
            v-for="option in roleOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
      <button class="invite-btn" @click="sendInvite" :disabled="isLoading">
        <span v-if="!isLoading">{{ $t("List.invite") }}</span>
        <span v-else>
          <i class="spinner"></i>
        </span>
      </button>
    </div>

    <div class="user-list">
      <span>{{ $t("List.peopleWithAccess") }}</span>
      <ul>
        <li
          v-for="data in listUsersAccess"
          :key="data.user.id"
          class="user-item"
        >
          <div class="user-info">
            <img
              src="@/assets/img/avatar/avatar3.png"
              alt="Avatar"
              class="user-avatar"
            />
            <div class="user-details">
              <span class="user-name">
                {{ data.user.name }}
                <span v-if="isCurrentUser(data.user.id)" class="current-user"
                  >({{ $t("Custom.me") }})</span
                >
              </span>
              <span class="user-email">{{ data.user.email }}</span>
            </div>
          </div>
          <div class="user-actions">
            <div class="user-role">
              <div
                :class="[
                  'btn-state',
                  data.isOwner
                    ? 'bg-frozen'
                    : getPermissionClass(data.sharingPermissionId),
                ]"
              >
                {{
                  data.isOwner
                    ? "Owner"
                    : getPermissionText(data.sharingPermissionId)
                }}
              </div>
            </div>
            <div
              v-if="!data.isOwner"
              class="user-remove"
              @click="removeUserAccess(data)"
            >
              <span class="material-symbols-outlined remove-icon">{{
                $t("Custom.remove")
              }}</span>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </Modal>

  <AppInfo
    v-if="isAppInfoVisible"
    :appData="appData"
    :appCountries="appCountries"
    :isBack="true"
    @get-app="fetchData"
    @close="handleBack"
  />

  <AreYouSureModal
    v-if="showListModal"
    :isVisible="showListModal"
    :title="modalListTitle"
    :message="modalListMessage"
    :itemId="selectedListId"
    :confirmButtonLabel="$t('Custom.delete')"
    :cancelButtonLabel="$t('Custom.cancel')"
    @result="handleModalListResult"
  />

  <AreYouSureModal
    v-if="showModal"
    :isVisible="showModal"
    :title="modalTitle"
    :message="modalMessage"
    :itemId="selectedAppId"
    :confirmButtonLabel="$t('Custom.remove')"
    :cancelButtonLabel="$t('Custom.cancel')"
    @result="handleModalResult"
  />

  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    @closed="!notification.show"
  />
</template>

<script>
import Loading from "@/components/user/layout/LoadingComponent.vue";
import NotFoundContent from "@/components/user/common/NotFoundContent.vue";
import ListCard from "@/components/user/common/ListCard.vue";
import SearchAndFilter from "@/components/user/common/SearchAndFilter.vue";
import Modal from "@/components/custom/modals/ModalComponent.vue";
import TextInput from "@/components/custom/inputs/TextInput.vue";
import SelectInput from "@/components/custom/inputs/SelectInput.vue";
import PlatformTypeEnum from "@/enums/PlatformTypeEnum";
import ListTypeEnum from "@/enums/ListTypeEnum";
import ItemCard from "@/components/user/common/ItemCard.vue";
import ButtonComponent from "@/components/custom/buttons/ButtonComponent.vue";
import AppInfo from "@/components/user/viewComponent/app/AppInfo.vue";
import SharingPermissionEnum from "@/enums/SharingPermissionEnum";
import AreYouSureModal from "@/components/custom/modals/AreYouSureModal.vue";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import FilterComponent from "@/components/user/common/FilterComponent.vue";
import MultiSelectFilterComponent from "@/components/user/common/MultiSelectFilterComponent.vue";
import { getAllList, getListApps, removeAppFromList } from "@/api/appList.api";
import { getApp, getAppById, getAppCountries } from "@/api/app.api";
import {
  createList,
  updateList,
  deleteList,
  addListAccessToUser,
  getListUsers,
  removeListAccessFromUser,
  updateNotification,
} from "@/api/list.api";
import { getUserByEmail } from "@/api/user.api";
import TableComponent from "@/components/custom/table/TableComponent.vue";
import { storeImage } from "@/ultis/user/functions/storePlatform";
import PaginationComponent from "@/components/user/common/PaginationComponent.vue";

export default {
  name: "AppListComponent",
  components: {
    Loading,
    NotFoundContent,
    ListCard,
    SearchAndFilter,
    Modal,
    TextInput,
    SelectInput,
    ItemCard,
    ButtonComponent,
    AppInfo,
    AreYouSureModal,
    NotificationComponent,
    FilterComponent,
    MultiSelectFilterComponent,
    TableComponent,
    PaginationComponent,
  },
  data() {
    return {
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null,
      },
      modalListTitle: "",
      modalListMessage: "",
      modalMessage: "",
      userAppList: [],
      isList: true,
      searchQuery: "",
      searchItemQuery: "",
      sortOrder: "asc",
      platformTypes: [],
      isModalOpen: false,
      isItemsModalOpen: false,
      modalTitle: "",
      actionType: "",
      listName: "",
      selectedOption: "",
      menuId: null,
      showListModal: false,
      appMenuId: null,
      listNameError: false,
      selectedOptionError: false,
      listItems: [],
      loading: true,
      isAppInfoVisible: false,
      appData: null,
      appCountries: null,
      sharingPermissionId: null,
      selectedAppId: null,
      showModal: false,
      selectedListId: null,
      ShareModal: false,
      roleOptions: [],
      isLoading: false,
      inviteEmail: "",
      inviteRole: null,
      listUsersAccess: [],
      currentUserId: null,
      sortCriteria: "created_at",
      sortOrder: "desc",
      sortingOptions: [
        { value: "name", label: this.$t("Filter.name") },
        { value: "isOwner", label: this.$t("Filter.owner") },
        { value: "appCount", label: this.$t("Filter.appCount") },
        { value: "created_at", label: this.$t("Filter.createdAt") },
      ],
      orderOptions: [
        { value: "asc", label: this.$t("Filter.ascending") },
        { value: "desc", label: this.$t("Filter.descending") },
      ],
      showFilterDropdown: false,
      selectedFilters: [],
      platformFilters: PlatformTypeEnum.getAllOptions(),
      tableHeaders: [
        { key: "index", label: this.$t("Table.index") },
        { key: "platform", label: this.$t("Table.platform") },
        { key: "name", label: this.$t("Table.name") },
        { key: "appCount", label: this.$t("Table.appCount") },
        { key: "permission", label: this.$t("Table.permission") },
        { key: "createdAt", label: this.$t("Table.createdAt") },
      ],
      loadingIndex: null,
    };
  },
  computed: {
    filteredAndSortedItems() {
      let items = [...this.userAppList];

      // if (this.searchQuery) {
      //   items = items.filter((item) =>
      //     item.list.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      //   );
      // }

      return items;
      if (this.selectedFilters.length > 0) {
        items = items.filter((item) =>
          this.selectedFilters.includes(item.list.platformTypeId)
        );
      }
      return items.sort((a, b) => {
        const modifier = this.sortOrder === "asc" ? 1 : -1;

        switch (this.sortCriteria) {
          case "name":
            return a.list.name.localeCompare(b.list.name) * modifier;
          case "isOwner":
            return (a.isOwner - b.isOwner) * modifier;
          case "appCount":
            return (a.list.appCount - b.list.appCount) * modifier;
          case "createdAt":
            const createdAtA = new Date(a.list.created_at).getTime();
            const createdAtB = new Date(b.list.created_at).getTime();
            if (isNaN(createdAtA) || isNaN(createdAtB)) return 0;
            return (createdAtA - createdAtB) * modifier;
          default:
            return 0;
        }
      });
    },
    filteredAndSortedListItems() {
      let filteredItems = this.listItems.filter((data) =>
        data.item.name
          .toLowerCase()
          .includes(this.searchItemQuery.toLowerCase())
      );
      return filteredItems.sort((a, b) => {
        let modifier = this.sortOrder === "asc" ? 1 : -1;
        return a.item.name.localeCompare(b.item.name) * modifier;
      });
    },
  },
  methods: {
    permissionClass(sharingPermissionId) {
      switch (sharingPermissionId) {
        case SharingPermissionEnum.ALL:
          return "bg-accepted";
        case SharingPermissionEnum.READ:
          return "bg-hold";
        case SharingPermissionEnum.EDIT:
          return "bg-edit";
        case SharingPermissionEnum.DELETE:
          return "bg-rejected";
        default:
          return "bg-rejected";
      }
    },
    permissionText(sharingPermissionId) {
      return SharingPermissionEnum.fromId(sharingPermissionId) || "Unknown";
    },
    getStoreImage(platformTypeId) {
      return storeImage(platformTypeId);
    },
    formatDate(date) {
      if (!date) return "";
      const locale = localStorage.getItem("localeTime") || "en-US";
      return new Date(date).toLocaleDateString(locale);
    },
    async handlePlatformFilterChange(filters) {
      this.selectedFilters = filters;
      this.loading = true;
      await this.fetchAppList();
    },
    async handleFilterChange({ criteria, order }) {
      this.sortCriteria = criteria;
      this.sortOrder = order;
      this.loading = true;
      await this.fetchAppList();
    },
    async handleSearchFilterChange() {
      this.loading = true;
      await this.fetchAppList();
    },
    handleBack() {
      this.isAppInfoVisible = false;
      this.appData = null;
      this.appCountries = null;
    },
    async fetchAppList(page = 1) {
      try {
        const response = await getAllList(page, this.sortCriteria, this.sortOrder, this.selectedFilters, this.searchQuery);
        if (response.data.status) {
          this.userAppList = response.data.data.data;
          this.pagination = response.data.data.meta;
        } else {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          response.data.msg
        );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async createList() {
      this.loading = true;
      try {
        const response = await createList(
          this.listName,
          this.selectedOption ? this.selectedOption : PlatformTypeEnum.ALL,
          ListTypeEnum.APP,
          false
        );
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.fetchAppList();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
      this.loading = false;
    },
    async updateList() {
      this.loading = true;
      try {
        const response = await updateList(this.currentListId, this.listName);

        if (response.data.status) {
          await this.fetchAppList();
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
      this.loading = false;
    },
    async handleModalAction(actionType) {
      if (actionType === "create") {
        this.createList();
      } else {
        this.updateList();
      }
      this.closeModal();
    },
    async getListApps(listId) {
      this.loading = true;
      try {
        const response = await getListApps(listId);

        if (response.data.status) {
          this.listItems = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async handleShow(storeId) {
      this.$router.push({
        name: "App",
        params: { storeId: storeId },
      });
    },
    async fetchData(url = this.url) {
      this.loading = true;
      try {
        await this.processAppLink(url);
        await this.getAppCountry();
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async processAppLink(url = this.url) {
      if (!url) return;
      try {
        const response = await getApp(url);

        if (response.data.status) {
          this.appData = response.data.data;
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
      }
    },
    async getAppCountry() {
      if (this.appCountries && this.appCountries.length > 0) {
        return;
      }
      try {
        const payload =
          this.appData.country.countryShortName === "US"
            ? { appId: this.appData.app.id }
            : { appId: this.appData.app.appId };

        const response = await getAppCountries(payload.appId);

        if (response.data.status) {
          this.appCountries = response.data.data.map((item) => ({
            ...item.country,
            appUrl: item.appUrl,
          }));
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async removeApp(appId, listId) {
      this.loading = true;
      try {
        const response = await removeAppFromList(appId, listId);
        if (response.data.status) {
          await this.fetchAppList();
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async deleteList(listId) {
      this.loading = true;
      try {
        const response = await deleteList(listId);
        if (response.data.status) {
          await this.fetchAppList();
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchListUsers(listId) {
      this.loading = true;
      try {
        const response = await getListUsers(listId);
        if (response.data.status) {
          this.listUsersAccess = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async getUserIdByEmail(email) {
      try {
        const response = await getUserByEmail(email);
        if (response.data.status) {
          return response.data.data.id;
        } else if (response.data.errNum === 404) {
          this.showNotification("hold", "Error", response.data.msg);
          return null;
        }
      } catch (error) {
        this.showNotification(
          "hold",
          "Error",
          error.response?.data?.msg || "Failed to fetch user ID."
        );
        return null;
      }
    },
    async addAccess(listId, userId, sharingPermissionId) {
      try {
        const response = await addListAccessToUser(
          listId,
          userId,
          sharingPermissionId,
          false
        );
        if (response.data.status) {
          await this.fetchListUsers(listId);
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async handleNotification(listId, userId, isNotification, index) {
      this.loadingIndex = index;
      try {
        const response = await updateNotification(
          listId,
          userId,
          !isNotification
        );
        if (response.data.status) {
          await this.fetchAppList();
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
        this.loadingIndex = null;
      } finally {
        this.loadingIndex = null;
      }
    },
    async showItems(listId, sharingPermissionId, listName) {
      this.selectedListId = listId;
      await this.getListApps(listId);
      this.modalTitle = listName;
      this.isItemsModalOpen = true;
      this.sharingPermissionId = sharingPermissionId;
    },
    shareList(listId) {
      this.modalTitle = this.$t("List.shareUserList");
      this.currentListId = listId;
      this.fetchListUsers(listId);
      this.ShareModal = true;
    },
    openModal(type, item = null) {
      this.modalTitle =
        type === "create" ? this.$t("List.create") : this.$t("List.edit");
      this.actionType = type;
      if (type === "update" && item) {
        this.currentListId = item.list.id;
        this.listName = item.list.name;
        this.selectedOption = item.list.platformTypeId;
      }
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.isItemsModalOpen = false;
      this.ShareModal = false;
      this.listItems = [];
      this.listName = "";
      this.selectedOption = "";
      this.currentListId = null;
      this.sharingPermissionId = null;
      this.showListModal = false;
    },
    setOpenAppMenu(menuId) {
      this.appMenuId = this.appMenuId === menuId ? null : menuId;
    },
    setMenuId(menuId) {
      this.menuId = this.menuId === menuId ? null : menuId;
    },
    generateButtons(data) {
      return [
        {
          label: this.$t("Custom.show"),
          icon: "visibility",
          disable: false,
          action: () => this.handleShow(data.item.storeId),
        },
        {
          label: this.$t("Custom.remove"),
          icon: "remove",
          disable: this.isDisabled("remove"),
          action: () => {
            this.handleAreYouSureModal(data.item),
              this.setOpenAppMenu(data.item.id);
          },
        },
      ];
    },
    permissionAccess(sharingPermissionId) {
      const allowedPermissions = [
        SharingPermissionEnum.ALL,
        SharingPermissionEnum.EDIT,
      ];
      return allowedPermissions.includes(sharingPermissionId);
    },
    isDisabled(button) {
      const permission = this.sharingPermissionId;
      switch (button) {
        case "show":
          return ![
            SharingPermissionEnum.ALL,
            SharingPermissionEnum.EDIT,
          ].includes(permission);
        case "showItems":
          return ![
            SharingPermissionEnum.ALL,
            SharingPermissionEnum.READ,
            SharingPermissionEnum.EDIT,
          ].includes(permission);
        case "share":
          return ![
            SharingPermissionEnum.ALL,
            SharingPermissionEnum.EDIT,
          ].includes(permission);
        case "remove":
          return !(permission === SharingPermissionEnum.ALL);
        default:
          return true;
      }
    },
    handleAreYouSureListModal(item) {
      this.closeModal();
      this.modalListTitle = this.$t("Custom.areYouSure");
      this.modalListMessage =
        this.$t("Custom.doYouWantDeleteThis") +
        "<b> " +
        item.name +
        "</b> ? " +
        this.$t("Custom.thisActionCannotBeUndone");
      this.selectedListId = item.id;
      this.showListModal = true;
    },
    handleModalListResult(result) {
      this.showListModal = false;
      if (result) {
        this.deleteList(this.selectedListId);
      }
    },
    handleAreYouSureModal(item) {
      this.closeModal();
      this.modalTitle = this.$t("Custom.areYouSure");
      this.modalMessage =
        this.$t("Custom.doYouWantRemoveThis") +
        "<b> " +
        item.name +
        "</b> ? " +
        this.$t("Custom.thisActionCannotBeUndone");
      this.selectedAppId = item.id;
      this.showModal = true;
    },
    handleModalResult(result, appId) {
      this.showModal = false;
      if (result) {
        this.removeApp(appId, this.selectedListId);
      }
      this.selectedListId = null;
    },
    fetchPlatformTypes() {
      this.platformTypes = PlatformTypeEnum.getAllOptions(true);
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
    async sendInvite() {
      this.isLoading = true;
      try {
        if (!this.isValidEmail(this.inviteEmail)) {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            this.$t("Validation.validEmail")
          );
          return;
        }
        if (!this.inviteEmail || !this.inviteRole) {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            this.$t("Validation.fieldCannotBeEmpty")
          );
          return;
        }
        if (this.isEmailAlreadyInvited(this.inviteEmail)) {
          this.showNotification(
            "hold",
            this.$t("Custom.error"),
            this.$t("Custom.thisEmailIsAlreadyInvited")
          );
          return;
        }
        const userId = await this.getUserIdByEmail(this.inviteEmail);
        if (!userId) {
          return;
        }
        await this.addAccess(this.currentListId, userId, this.inviteRole);
        await this.fetchListUsers(this.currentListId);
        this.inviteEmail = "";
        this.inviteRole = this.roleOptions[0].id;
      } catch (error) {
      } finally {
        this.isLoading = false;
      }
    },
    getPermissionClass(permissionId) {
      switch (permissionId) {
        case SharingPermissionEnum.ALL:
          return "bg-accepted";
        case SharingPermissionEnum.READ:
          return "bg-hold";
        case SharingPermissionEnum.EDIT:
          return "bg-edit";
        case SharingPermissionEnum.DELETE:
          return "bg-rejected";
        default:
          return "bg-rejected";
      }
    },
    getPermissionText(permissionId) {
      switch (permissionId) {
        case SharingPermissionEnum.ALL:
          return "All";
        case SharingPermissionEnum.READ:
          return "Read";
        case SharingPermissionEnum.EDIT:
          return "Edit";
        default:
          return "Unknown";
      }
    },
    isCurrentUser(userId) {
      return userId === this.currentUserId;
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    isEmailAlreadyInvited(email) {
      return this.listUsersAccess.some((data) => data.user.email === email);
    },
    async removeUserAccess(data) {
      this.loading = true;
      try {
        const response = await removeListAccessFromUser(
          data.listId,
          data.userId
        );
        if (response.data.status) {
          await this.fetchListUsers(data.listId);
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    toggleFilterDropdown() {
      this.showFilterDropdown = !this.showFilterDropdown;
    },
    handleClickOutside(event) {
      if (
        this.$refs.filterDropdown &&
        !this.$refs.filterDropdown.contains(event.target)
      ) {
        this.showFilterDropdown = false;
      }
    },
  },
  async mounted() {
    await Promise.all([this.fetchAppList(), this.fetchPlatformTypes()]);
    this.roleOptions = SharingPermissionEnum.allOptions(false);
    if (this.roleOptions.length > 0) {
      this.inviteRole = this.roleOptions[0].id;
    }
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
/** Filter */
.right-strip {
  display: flex;
}
.filter-container {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
}

.strip {
  overflow-x: visible !important;
}

.item-card {
  padding: 15px !important;
  margin-bottom: 8px;
}
/** */
.invite-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.input-select-container {
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db; /* Hafif gri kenarlık */
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
}

.invite-input {
  flex: 1;
  border: none;
  padding: 10px;
  font-size: 14px;
  outline: none;
}

.role-select {
  margin: 8px 5px;
  cursor: pointer;
  border: none;
}

.invite-btn {
  padding: 10px 20px;
  font-size: 14px;
  background-color: black;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  height: 100%;
}

.invite-btn:hover {
  background-color: rgb(36, 36, 36);
}

.user-list {
  margin-top: 20px;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-details {
  display: flex;
  flex-direction: column; /* Alt alta dizilim */
  gap: 4px;
}

.user-actions {
  display: flex;
  gap: 10px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.user-name {
  font-size: 14px;
  font-weight: bold;
}

.user-email {
  font-size: 14px;
}

.current-user {
  font-size: 12px;
  font-style: italic;
  color: gray;
  margin-left: 5px;
}

.user-role select {
  padding: 6px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.user-remove {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.spinner {
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-left-color: #000;
  border-radius: 50%;
  width: 1em;
  height: 1em;
  animation: spin 0.6s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.invite-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
